import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { IncomeCallService } from 'src/app/_Common/_Services/income-call.service';
import { UserService } from 'src/app/_Common/_Services/user.service';
import { IgnosisTransferService } from './ignosis-transfer.service';

const OFFER_MS = 5 * 60 * 1000;

type Invite = {
  url: string;
  frameOrigin: string;
  roomName: string;
  token: string;
  reason: string | null;
  dueId: number | null;
};

@Component({
  selector: 'mt-ignosis-transfer',
  templateUrl: './ignosis-transfer.component.html',
  styleUrls: ['./ignosis-transfer.component.scss'],
  standalone: false
})
export class IgnosisTransferComponent implements OnInit, OnDestroy {
  @ViewChild('frame') frame?: ElementRef<HTMLIFrameElement>;

  invite: Invite | null = null;
  frameUrl: SafeResourceUrl | null = null;
  state: 'offered' | 'joined' | 'ended' | 'error' = 'offered';
  notice = '';
  left = 16;
  top = 80;
  dragging = false;
  private sentToken = '';
  private tabId = Math.random().toString(36).slice(2);
  private timer: ReturnType<typeof setTimeout> | null = null;
  private subscriptions: Subscription[] = [];
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  constructor(
    private incomeCall: IncomeCallService,
    private userService: UserService,
    private transfer: IgnosisTransferService,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const held = localStorage.getItem('ignosis-transfer-tab');
    if (held && !held.startsWith(this.tabId + ':')) localStorage.removeItem('ignosis-transfer-tab');
    this.incomeCall.intializeIncomeSocket();
    this.transfer.reportDialerBusy(false);
    const join = () => {
      const userId = this.transfer.sessionUserId$.value || this.userService.getLoggedInUserId();
      if (userId) this.incomeCall.joinIgnosisRoom('ignosis-' + String(userId));
    };
    join();
    const joinTimer = setInterval(join, 2000);
    this.subscriptions.push(this.transfer.sessionUserId$.subscribe(() => join()));
    this.subscriptions.push(new Subscription(() => clearInterval(joinTimer)));
    this.subscriptions.push(
      this.incomeCall.onIgnosisTransferOffer().subscribe((data) => {
        this.zone.run(() => this.onOffer(data));
      })
    );
    window.addEventListener('message', this.onMessage);
    window.addEventListener('mousemove', this.onDragMove);
    window.addEventListener('mouseup', this.onDragEnd);
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('mousemove', this.onDragMove);
    window.removeEventListener('mouseup', this.onDragEnd);
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.clearTimer();
  }

  startDrag(event: MouseEvent) {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest('a, button')) return;
    const el = (event.currentTarget as HTMLElement).closest('.ignosis-transfer') as HTMLElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    this.left = rect.left;
    this.top = rect.top;
    this.dragOffsetX = event.clientX - rect.left;
    this.dragOffsetY = event.clientY - rect.top;
    this.dragging = true;
    event.preventDefault();
  }

  leave() {
    this.postToFrame('humanCalling:hangup');
    this.finish('ended', '');
  }

  decline() {
    this.postToFrame('humanCalling:hangup');
    this.finish('ended', '');
  }

  private onDragMove = (event: MouseEvent) => {
    if (!this.dragging) return;
    const width = 320;
    const height = 360;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const maxTop = Math.max(8, window.innerHeight - height - 8);
    this.left = Math.min(maxLeft, Math.max(8, event.clientX - this.dragOffsetX));
    this.top = Math.min(maxTop, Math.max(8, event.clientY - this.dragOffsetY));
    this.cdr.detectChanges();
  };

  private onDragEnd = () => {
    this.dragging = false;
  };

  private onOffer(data: any) {
    localStorage.removeItem('ignosis-transfer-tab');
    const invite = this.readInvite(data);
    if (!invite) {
      console.warn('ignosis transfer offer ignored', data);
      return;
    }
    console.log('ignosis transfer popup open', {
      roomName: invite.roomName,
      dueId: invite.dueId,
      expired: isTokenExpired(invite.token)
    });
    this.claimTab(invite.roomName);
    const sameFrame = this.invite && this.invite.url === invite.url && this.frameUrl;
    this.invite = invite;
    this.notice = '';
    this.state = 'offered';
    this.left = 16;
    this.top = Math.max(8, Math.min(120, window.innerHeight - 380));
    if (isTokenExpired(invite.token)) {
      this.sentToken = '';
      this.frameUrl = null;
      this.notice = 'Transfer invite expired. Decline and wait for a new offer.';
      this.armTimer();
      this.cdr.detectChanges();
      return;
    }
    if (!sameFrame) {
      this.sentToken = '';
      this.frameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(invite.url);
    } else if (this.sentToken && this.sentToken !== invite.token) {
      this.sentToken = '';
      this.sendInvite();
    }
    this.armTimer();
    this.cdr.detectChanges();
  }

  private readInvite(data: any): Invite | null {
    if (!data || !data.url || !data.roomName || !data.token) return null;
    const frameOrigin = originOf(data.url);
    if (!frameOrigin) return null;
    const dueId = data.dueId != null && data.dueId !== '' ? Number(data.dueId) : null;
    return {
      url: data.url,
      frameOrigin,
      roomName: data.roomName,
      token: data.token,
      reason: data.reason || null,
      dueId: Number.isFinite(dueId as number) ? (dueId as number) : null
    };
  }

  private onMessage = (event: MessageEvent) => {
    if (!this.invite || event.origin !== this.invite.frameOrigin) return;
    const data = event.data as { event?: string; message?: string } | null;
    if (!data || typeof data.event !== 'string') return;
    this.zone.run(() => {
      if (data.event === 'humanCalling:ready') {
        if (isTokenExpired(this.invite!.token)) {
          this.finish('error', 'Transfer invite expired. Wait for a new offer.');
          return;
        }
        this.sendInvite();
        return;
      }
      if (data.event === 'humanCalling:joined') {
        this.state = 'joined';
        this.clearTimer();
        this.transfer.joined(this.invite!.roomName);
        this.cdr.detectChanges();
        return;
      }
      if (data.event === 'humanCalling:ended') {
        this.finish('ended', 'The customer left the call.');
        return;
      }
      if (data.event === 'humanCalling:error') {
        const raw = data.message || '';
        const expired = /token is expired|invalid token/i.test(raw);
        this.finish(
          'error',
          expired ? 'Transfer invite expired. Wait for a new offer.' : shortError(raw) || 'The transfer could not be joined.'
        );
      }
    });
  };

  private sendInvite() {
    if (!this.invite || !this.frame || this.sentToken === this.invite.token) return;
    if (isTokenExpired(this.invite.token)) {
      this.finish('error', 'Transfer invite expired. Wait for a new offer.');
      return;
    }
    this.frame.nativeElement.contentWindow?.postMessage(
      {
        event: 'humanCalling:invite',
        invite: {
          token: this.invite.token,
          roomName: this.invite.roomName,
          reason: this.invite.reason
        }
      },
      this.invite.frameOrigin
    );
    this.sentToken = this.invite.token;
  }

  private postToFrame(event: string) {
    if (!this.invite || !this.frame) return;
    this.frame.nativeElement.contentWindow?.postMessage({ event }, this.invite.frameOrigin);
  }

  private finish(state: 'ended' | 'error', notice: string) {
    const roomName = this.invite?.roomName;
    this.clearTimer();
    if (roomName) {
      this.transfer.release(roomName);
      this.releaseTab(roomName);
    }
    this.invite = null;
    this.frameUrl = null;
    this.sentToken = '';
    this.state = state;
    this.notice = notice;
    if (notice) {
      setTimeout(() => {
        if (!this.invite) this.notice = '';
        this.cdr.detectChanges();
      }, 4000);
    }
    this.cdr.detectChanges();
  }

  private armTimer() {
    this.clearTimer();
    this.timer = setTimeout(() => {
      if (this.state === 'offered') this.decline();
    }, OFFER_MS);
  }

  private clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private claimTab(roomName: string) {
    localStorage.setItem('ignosis-transfer-tab', this.tabId + ':' + roomName);
  }

  private releaseTab(roomName: string) {
    const key = 'ignosis-transfer-tab';
    if (localStorage.getItem(key) === this.tabId + ':' + roomName) localStorage.removeItem(key);
  }
}

function originOf(url: string) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.origin : '';
  } catch (e) {
    return '';
  }
}

function isTokenExpired(token: string) {
  try {
    const part = token.split('.')[1];
    if (!part) return false;
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    const payload = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload || typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now();
  } catch (e) {
    return false;
  }
}

function shortError(message: string) {
  if (!message) return '';
  if (/token is expired|invalid token/i.test(message)) return 'Transfer invite expired. Wait for a new offer.';
  return message.length > 120 ? message.slice(0, 117) + '...' : message;
}
